import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Handshake, Loader2, ChevronLeft, TrendingUp, FileText, Video,
} from "lucide-react";

const WINDOW_LABELS = {
  theatrical:     "Theatrical",
  home_video:     "Home Video",
  satellite_ott:  "Satellite · OTT",
  overseas:       "Overseas",
  music:          "Music",
  non_theatrical: "Non-Theatrical",
};
const STATUS_TONE = {
  negotiating: "bg-amber-500/20 text-amber-200",
  signed:      "bg-sky-500/20 text-sky-200",
  delivered:   "bg-emerald-500/20 text-emerald-200",
  recouped:    "bg-emerald-500/25 text-emerald-100",
  cancelled:   "bg-red-500/20 text-red-200",
};

/**
 * Producer's read-only mirror of every deal a sales agent has logged
 * against films the producer owns. Powered by the `film_producer_org_id`
 * link on each deal — sales agents pick the producer's org when logging.
 * Producer never edits from here — it's a transparency window.
 */
export default function ProducerSalesDeals() {
  const [loading, setLoading] = useState(true);
  const [deals, setDeals]     = useState([]);
  const [totals, setTotals]   = useState({});

  useEffect(() => {
    api.get("/producer/sales-deals/mine")
      .then(({ data }) => { setDeals(data.deals || []); setTotals(data.totals || {}); })
      .catch(() => { setDeals([]); setTotals({}); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="producer-sales-deals-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="producer-sales-deals-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Producer · Read-Only</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2 text-orange-300"
            data-testid="producer-sales-deals-heading">
          Sales Ledger
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Every rights sale a sales agent has closed on your slate.
          MG, expenses, commission split and status — all in real time.
          Read-only mirror of the agent&apos;s own ledger.
        </p>
      </div>

      {/* Totals per currency */}
      {Object.keys(totals).length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="producer-sales-deals-totals">
          {Object.entries(totals).map(([cur, t]) => (
            <div key={cur} className="cs-card rounded-xl p-5"
                 data-testid={`producer-sales-deals-totals-${cur}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">{cur} · {t.count} deal{t.count === 1 ? "" : "s"}</div>
                <TrendingUp size={14} className="text-emerald-300"/>
              </div>
              <div className="mt-3 font-display text-2xl uppercase tracking-tight text-emerald-200">
                {fmtMoney(t.producer, cur)}
              </div>
              <div className="text-[10px] text-white/55 mt-1">Your net share</div>
              <div className="mt-3 pt-3 border-t border-white/8 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Total MG</div>
                  <div className="mt-0.5 text-sm text-white">{fmtMoney(t.mg, cur)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Agent commission</div>
                  <div className="mt-0.5 text-sm text-white">{fmtMoney(t.agent, cur)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="producer-sales-deals-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading deals…
          </div>
        ) : deals.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="producer-sales-deals-empty">
            <Handshake size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No sales-agent deals logged yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              When any sales agent on Call Sheet logs a deal and links your
              production house as the producer, the sale appears here — with
              live MG, expenses and split visibility.
            </div>
          </div>
        ) : (
          <div className="grid gap-4"
               data-testid="producer-sales-deals-list">
            {deals.map((d) => <DealRow key={d.id} deal={d}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtMoney(n, cur) {
  return `${cur} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function DealRow({ deal }) {
  return (
    <div className="cs-card rounded-xl p-5"
         data-testid={`producer-sales-deal-${deal.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg uppercase tracking-tight">{deal.film_title}</div>
            {deal.film_year && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.16em] uppercase text-white/60">
                {deal.film_year}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold ${STATUS_TONE[deal.status] || "bg-white/8 text-white/60"}`}
                  data-testid={`producer-sales-deal-status-${deal.id}`}>
              {deal.status}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-white/60">
            Sold by <span className="text-orange-300 font-semibold">{deal.sales_agent_name || "Sales Agent"}</span> to <span className="text-white/85 font-semibold">{deal.buyer}</span> ({deal.buyer_type}) · {WINDOW_LABELS[deal.window_kind]} rights for {deal.territory}
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-white/8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SplitStat label="MG"           value={fmtMoney(deal.mg_amount, deal.mg_currency)}/>
        <SplitStat label="Expenses"     value={fmtMoney(deal.expenses_amount, deal.expenses_currency)}/>
        <SplitStat label={`Agent ${deal.commission_pct}%`}
                   value={fmtMoney(deal.agent_share, deal.mg_currency)}/>
        <SplitStat label="Your share"   value={fmtMoney(deal.producer_share, deal.mg_currency)} accent="emerald"/>
      </div>
      {(deal.signed_date || deal.delivery_deadline || deal.notes) && (
        <div className="mt-3 pt-3 border-t border-white/6 text-[11px] text-white/55 flex flex-wrap gap-4">
          {deal.signed_date && <span>Signed · {deal.signed_date}</span>}
          {deal.delivery_deadline && <span>Deliver by · {deal.delivery_deadline}</span>}
          {deal.notes && <span className="italic text-white/45">&ldquo;{deal.notes}&rdquo;</span>}
        </div>
      )}
      {(deal.contract_url || deal.screener_link_id) && (
        <div className="mt-3 pt-3 border-t border-white/6 flex flex-wrap gap-3 items-center"
             data-testid={`producer-sales-deal-attachments-${deal.id}`}>
          {deal.contract_url && (
            <a href={deal.contract_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-200 text-[10px] tracking-[0.18em] uppercase hover:bg-emerald-500/25 transition"
               data-testid={`producer-sales-deal-contract-${deal.id}`}>
              <FileText size={11}/> Signed Contract
              {deal.contract_signed_date && (
                <span className="text-white/50 normal-case tracking-normal">· {deal.contract_signed_date}</span>
              )}
            </a>
          )}
          {deal.screener_link_id && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-500/15 text-indigo-200 text-[10px] tracking-[0.18em] uppercase"
                  data-testid={`producer-sales-deal-screener-${deal.id}`}>
              <Video size={11}/> Screener attached
            </span>
          )}
        </div>
      )}
    </div>
  );
}
function SplitStat({ label, value, accent }) {
  const clr = accent === "emerald" ? "text-emerald-200 font-semibold" : "text-white";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className={`mt-0.5 text-sm ${clr}`}>{value}</div>
    </div>
  );
}
